import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocType } from "../types";

const ESTIMATED_TIME_PER_STEP_SECONDS = 10;

interface DocumentsGenerationLoaderProps {
  selectedDocs: DocType[];
  selectedLanguages: string[];
  onComplete: () => void;
}

export const DocumentsGenerationLoader: React.FC<
  DocumentsGenerationLoaderProps
> = ({ selectedDocs, selectedLanguages, onComplete }) => {
  const { t } = useTranslation();
  const [currentDocIndex, setCurrentDocIndex] = useState<number>(0);
  const [currentLangIndex, setCurrentLangIndex] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  // Effect for document and language progression
  useEffect(() => {
    if (currentDocIndex < selectedDocs.length) {
      const timer = setTimeout(() => {
        // If we've gone through all languages for this document
        if (currentLangIndex >= selectedLanguages.length - 1) {
          // Move to next document and reset language index
          setCurrentDocIndex((prev) => prev + 1);
          setCurrentLangIndex(0);
        } else {
          // Move to next language for current document
          setCurrentLangIndex((prev) => prev + 1);
        }
      }, ESTIMATED_TIME_PER_STEP_SECONDS * 1000);

      return () => clearTimeout(timer);
    } else if (!isCompleted) {
      // All documents processed
      setIsCompleted(true);
      // Call onComplete immediately
      onComplete();
    }
  }, [
    currentDocIndex,
    currentLangIndex,
    selectedDocs,
    selectedLanguages,
    isCompleted,
    onComplete,
  ]);

  // Effect for timer
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format time as mm:ss
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Calculate progress percentage
  const progressPercentage = Math.min(
    Math.round(
      (elapsedTime /
        (selectedDocs.length *
          selectedLanguages.length *
          ESTIMATED_TIME_PER_STEP_SECONDS)) *
        100
    ),
    100
  );

  // Current document and language being processed
  const currentDoc =
    currentDocIndex < selectedDocs.length
      ? selectedDocs[currentDocIndex]
      : null;
  const currentLang =
    currentLangIndex < selectedLanguages.length
      ? selectedLanguages[currentLangIndex]
      : null;

  return (
    <div className="mt-2 p-6 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-cyan-500 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {t("documents.generate.generating")}
        </h2>
        <div className="flex flex-col items-end">
          <div className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full flex items-center gap-1 mb-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formatTime(elapsedTime)} /{" "}
            {formatTime(
              selectedDocs.length *
                selectedLanguages.length *
                ESTIMATED_TIME_PER_STEP_SECONDS
            )}
          </div>
          <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-cyan-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Current generation status */}
      <div className="my-4 p-3 bg-cyan-50 rounded-lg border border-cyan-100">
        <div className="text-cyan-800 font-medium">
          {currentDoc && currentLang ? (
            <>
              <span className="text-cyan-600">
                {t("documents.generate.generating_document", {
                  document: t(`documents.${currentDoc}.label`),
                  language: t(`languages.${currentLang}`),
                })}
              </span>
              <div className="mt-1 text-sm text-cyan-600">
                {t("documents.generate.progress", {
                  current:
                    currentDocIndex * selectedLanguages.length +
                    currentLangIndex +
                    1,
                  total: selectedDocs.length * selectedLanguages.length,
                })}
              </div>
            </>
          ) : (
            <span className="text-cyan-600">
              {t("documents.generate.finalizing")}
            </span>
          )}
        </div>
      </div>

      {/* Document progress list */}
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {t("documents.generate.processing_documents")}
        </h3>
        <ul className="space-y-2 max-h-60 overflow-y-auto">
          {selectedDocs.map((doc, docIndex) => (
            <li key={doc} className="rounded-lg border p-2">
              <div
                className={`font-medium ${
                  docIndex < currentDocIndex
                    ? "text-green-600"
                    : docIndex === currentDocIndex
                    ? "text-cyan-600"
                    : "text-gray-400"
                }`}
              >
                {t(`documents.${doc}.label`)}
              </div>
              <div className="mt-1 pl-2">
                <ul className="space-y-1">
                  {selectedLanguages.map((lang, langIndex) => {
                    // Define the state for this language
                    const isCompleted =
                      docIndex < currentDocIndex ||
                      (docIndex === currentDocIndex &&
                        langIndex < currentLangIndex);
                    const isActive =
                      docIndex === currentDocIndex &&
                      langIndex === currentLangIndex;

                    return (
                      <li
                        key={`${doc}-${lang}`}
                        className={`flex items-center text-sm gap-2 py-1 px-2 rounded-md ${
                          isActive
                            ? "bg-cyan-50 text-cyan-700 animate-pulse"
                            : isCompleted
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {isCompleted ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-green-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : isActive ? (
                          <div className="h-2 w-2 bg-cyan-500 rounded-full animate-pulse"></div>
                        ) : (
                          <div className="h-2 w-2 bg-gray-300 rounded-full"></div>
                        )}
                        {t(`languages.${lang}`)}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
